import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Search, Phone, ArrowRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHero } from '@/components/common/PageHero';
import { ConfigurableHero } from '@/components/common/ConfigurableHero';
import { SEOHead } from '@/components/seo/SEOHead';
import { usePageDesign } from '@/hooks/usePageDesign';
import { useIndustrySlug } from '@/hooks/useIndustrySlug';

export const ServiceAreasPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const industrySlug = useIndustrySlug();
  const [regionFilter, setRegionFilter] = useState('all');
  const { heroStyle, showBreadcrumbs, showTrustBadges, showCtaButtons } = usePageDesign('service_areas_listing');

  const { data: locations = [], isLoading } = useQuery({
    queryKey: ['public-service-locations'],
    queryFn: async () => {
      const { data } = await pmsFrom('service_locations')
        .select('id, name, slug, region, region_id, description, is_primary')
        .eq('is_published', true)
        .order('sort_order', { ascending: true });
      return data || [];
    },
  });

  const { data: dbRegions = [] } = useQuery({
    queryKey: ['public-service-regions'],
    queryFn: async () => {
      const { data } = await pmsFrom('service_regions')
        .select('id, slug, name')
        .eq('is_active', true);
      return data || [];
    },
  });

  const { data: businessSettings } = useQuery({
    queryKey: ['business-settings-areas-page'],
    queryFn: async () => {
      const { data } = await pmsFrom('business_settings')
        .select('company_name, phone, address_city, address_state')
        .limit(1)
        .maybeSingle();
      return data;
    },
  });

  const companyName = businessSettings?.company_name || 'Our Company';
  const phone = businessSettings?.phone || '';
  const city = businessSettings?.address_city || '';
  const state = businessSettings?.address_state || '';

  const regions = useMemo(() => {
    const r = [...new Set(locations.map(l => l.region).filter(Boolean))] as string[];
    return r;
  }, [locations]);

  const filtered = useMemo(() => {
    let result = locations;
    if (regionFilter !== 'all') {
      result = result.filter(l => l.region === regionFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(l =>
        l.name.toLowerCase().includes(q) ||
        (l.description && l.description.toLowerCase().includes(q))
      );
    }
    return result;
  }, [locations, regionFilter, searchQuery]);

  return (
    <>
      <SEOHead
        title={`Service Areas | ${companyName}`}
        description={`${companyName} proudly serves ${city}${state ? `, ${state}` : ''} and ${locations.length}+ surrounding communities.`}
      />

      <ConfigurableHero
        heroStyle={heroStyle}
        title="Service Areas"
        subtitle={`Proudly serving ${city || 'your area'} and ${locations.length}+ surrounding communities with quality service.`}
        badge="Local Coverage"
        showBreadcrumbs={showBreadcrumbs}
        breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Service Areas' }]}
        showTrustBadges={showTrustBadges}
        showCtaButtons={showCtaButtons}
      >
        <div className="max-w-md mx-auto relative mt-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search your area..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-12 text-base bg-card border-border"
          />
        </div>
      </ConfigurableHero>

      {/* Compact Map Banner */}
      <section className="py-4">
        <div className="container mx-auto px-4">
          <div className="relative h-28 bg-muted rounded-xl overflow-hidden flex items-center justify-center gap-3">
            <MapPin className="h-8 w-8 text-primary" />
            <div>
              <p className="font-semibold">Serving {locations.length} communities across {city}{state ? `, ${state}` : ''}</p>
              <p className="text-sm text-muted-foreground">Find your area below or search above</p>
            </div>
          </div>
        </div>
      </section>

      {/* Region Tabs + Areas Grid */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-4">
              Areas We Serve
            </h2>
          </div>

          {regions.length > 1 && (
            <div className="flex justify-center mb-8">
              <Tabs value={regionFilter} onValueChange={setRegionFilter}>
                <TabsList>
                  <TabsTrigger value="all">All ({locations.length})</TabsTrigger>
                  {regions.map(r => (
                    <TabsTrigger key={r} value={r}>
                      {r} ({locations.filter(l => l.region === r).length})
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
          )}

          <p className="text-center text-muted-foreground mb-8">
            {filtered.length} service {filtered.length === 1 ? 'area' : 'areas'} found
          </p>

          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40" />)}
            </div>
          ) : filtered.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((area, index) => (
                <motion.div
                  key={area.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.03 }}
                  viewport={{ once: true }}
                >
                  <Link
                    to={(() => { const r = dbRegions.find(r => r.id === (area as any).region_id); return r ? `/${industrySlug}/${r.slug}/${area.slug}` : `/${industrySlug}/${area.slug}`; })()}
                    className="group block bg-card p-6 rounded-xl border border-border hover:border-primary/50 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary transition-colors duration-300">
                        <MapPin className="h-6 w-6 text-primary group-hover:text-primary-foreground transition-colors duration-300" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-heading font-semibold text-lg group-hover:text-primary transition-colors">
                            {area.name}
                          </h3>
                          {area.is_primary && (
                            <Badge variant="default" className="text-[10px]">HQ</Badge>
                          )}
                        </div>
                        {area.region && (
                          <Badge variant="outline" className="text-xs mb-2">{area.region}</Badge>
                        )}
                        {area.description && (
                          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{area.description}</p>
                        )}
                        <span className="inline-flex items-center text-sm font-medium text-primary">
                          View Details <ArrowRight className="h-4 w-4 ml-1" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No areas match your search.</p>
              <Button variant="ghost" className="mt-4" onClick={() => { setSearchQuery(''); setRegionFilter('all'); }}>
                Clear filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Don't See Your Area */}
      <section className="py-16 lg:py-24 bg-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-3xl font-bold mb-4">Don't See Your Area?</h2>
            <p className="text-muted-foreground mb-6">
              We may still be able to serve you. Contact us to inquire about service in your area.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {phone && (
                <Button asChild size="lg">
                  <a href={`tel:${phone.replace(/\D/g, '')}`} className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Call {phone}
                  </a>
                </Button>
              )}
              <Button asChild variant="outline" size="lg">
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Note */}
      <section className="py-12 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <p className="text-lg">
            Serving {city}{state ? `, ${state}` : ''} and {locations.length} surrounding communities.
          </p>
        </div>
      </section>
    </>
  );
};
